import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, MessageCircle, ArrowRight, ShieldCheck, Clock, Send, Loader2, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+0-9][0-9 ().-]{5,19}$/;

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const up = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = "Nom requis (min 2 caractères)";
    if (!form.email || !EMAIL_RE.test(form.email.trim())) e.email = "Email invalide";
    if (form.phone && !PHONE_RE.test(form.phone.trim())) e.phone = "Numéro de téléphone invalide";
    if (!form.subject || form.subject.trim().length < 3) e.subject = "Sujet requis (min 3 caractères)";
    if (!form.message || form.message.trim().length < 10) e.message = "Message trop court (min 10 caractères)";
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      toast.error("Veuillez corriger les champs en rouge");
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
        phone: form.phone.trim() || null,
      };
      const { data } = await api.post("/contact", payload);
      setSent(true);
      toast.success(data?.message || "Message enregistré — notre équipe le traitera pendant les horaires d’assistance.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="card-soft p-8 text-center" data-testid="contact-form-success">
        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-6 h-6 text-emerald-600" />
        </div>
        <h3 className="font-display font-bold text-2xl text-[#15302A] mb-2">Message envoyé !</h3>
        <p className="text-[#1F4032]/70 mb-5">Merci, notre équipe traitera votre demande pendant les horaires d’assistance.</p>
        <Button onClick={() => setSent(false)} variant="outline"
          className="rounded-full border-[#1F4032] text-[#1F4032]" data-testid="contact-form-reset">
          Envoyer un autre message
        </Button>
      </div>
    );
  }

  return (
    <form id="contact-form" onSubmit={submit} className="card-soft p-6 md:p-8" data-testid="contact-form" noValidate>
      <div className="overline text-[#1F4032]/60 mb-1">— Écrivez-nous</div>
      <h3 className="font-display font-bold text-2xl text-[#15302A] mb-5">Envoyer un message</h3>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="overline">Nom complet *</Label>
          <Input required minLength={2} value={form.name} onChange={up("name")}
            aria-invalid={!!errors.name}
            className={`rounded-md mt-1 h-11 ${errors.name ? "border-red-500" : ""}`} data-testid="contact-name" />
          {errors.name && <p className="text-xs text-red-600 mt-1" data-testid="contact-name-error">{errors.name}</p>}
        </div>
        <div>
          <Label className="overline">Email *</Label>
          <Input type="email" required value={form.email} onChange={up("email")}
            aria-invalid={!!errors.email}
            className={`rounded-md mt-1 h-11 ${errors.email ? "border-red-500" : ""}`} data-testid="contact-email-input" />
          {errors.email && <p className="text-xs text-red-600 mt-1" data-testid="contact-email-error">{errors.email}</p>}
        </div>
        <div>
          <Label className="overline">Téléphone (facultatif)</Label>
          <Input type="tel" value={form.phone} onChange={up("phone")}
            placeholder="06 12 34 56 78"
            aria-invalid={!!errors.phone}
            className={`rounded-md mt-1 h-11 ${errors.phone ? "border-red-500" : ""}`} data-testid="contact-phone-input" />
          {errors.phone && <p className="text-xs text-red-600 mt-1" data-testid="contact-phone-error">{errors.phone}</p>}
        </div>
        <div>
          <Label className="overline">Sujet *</Label>
          <Input required minLength={3} value={form.subject} onChange={up("subject")}
            placeholder="Ex : Problème sur une mission"
            aria-invalid={!!errors.subject}
            className={`rounded-md mt-1 h-11 ${errors.subject ? "border-red-500" : ""}`} data-testid="contact-subject" />
          {errors.subject && <p className="text-xs text-red-600 mt-1" data-testid="contact-subject-error">{errors.subject}</p>}
        </div>
      </div>
      <div className="mt-4">
        <Label className="overline">Message *</Label>
        <Textarea required minLength={10} maxLength={3000} value={form.message} onChange={up("message")}
          placeholder="Décrivez votre demande…"
          aria-invalid={!!errors.message}
          className={`rounded-md mt-1 min-h-32 ${errors.message ? "border-red-500" : ""}`} data-testid="contact-message" />
        {errors.message && <p className="text-xs text-red-600 mt-1" data-testid="contact-message-error">{errors.message}</p>}
      </div>
      <Button type="submit" disabled={loading}
        className="rounded-full bg-[#1F4032] hover:bg-[#2A5443] text-white h-11 px-6 mt-5 gap-2"
        data-testid="contact-submit">
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : <><Send className="w-4 h-4" /> Envoyer le message</>}
      </Button>
    </form>
  );
}

export default function Contact() {
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#1F4032]/10">
        <div aria-hidden className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-[#1F4032]/8 blur-3xl pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="overline text-[#1F4032]/60 mb-3">— CONTACT</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl md:text-6xl tracking-tighter text-[#15302A] mb-5">
              Parlons de <br /><span className="bg-gradient-to-br from-[#2A5443] via-[#1F4032] to-[#0F2A20] bg-clip-text text-transparent">votre transport.</span>
            </h1>
            <p className="text-lg text-[#1F4032]/70 max-w-2xl">
              Une question, un problème sur une mission, un partenariat ?
              Notre équipe française vous répond rapidement pendant les horaires d'assistance.
            </p>
            <p className="text-sm text-[#1F4032]/70 mt-3 flex items-center gap-2">
              <Clock className="w-4 h-4" /> Assistance : lundi–vendredi, 9h30–19h30 (heure de Paris).
              Les demandes hors horaires sont enregistrées.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact methods */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-5">
          {/* Email */}
          <a
            href="#contact-form"
            className="card-soft p-6 hover:shadow-lg hover:shadow-[#1F4032]/10 transition-all hover:-translate-y-1"
            data-testid="contact-email"
          >
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#1F4032] to-[#0F2A20] flex items-center justify-center mb-4 shadow-md shadow-[#1F4032]/25">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div className="overline text-[#1F4032]/60 mb-1">Email</div>
            <div className="font-display font-bold text-lg text-[#15302A] mb-2">Formulaire de contact</div>
            <div className="text-sm text-[#1F4032]/65 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> Demande enregistrée en ligne
            </div>
          </a>

          {/* Phone */}
          <a
            href="#contact-form"
            className="card-soft p-6 hover:shadow-lg hover:shadow-[#1F4032]/10 transition-all hover:-translate-y-1"
            data-testid="contact-phone"
          >
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#1F4032] to-[#0F2A20] flex items-center justify-center mb-4 shadow-md shadow-[#1F4032]/25">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <div className="overline text-[#1F4032]/60 mb-1">Téléphone</div>
            <div className="font-display font-bold text-lg text-[#15302A] mb-2">Coordonnées à confirmer</div>
            <div className="text-sm text-[#1F4032]/65 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> Lun–Ven · 9 h 30 – 19 h 30
            </div>
          </a>

          {/* Live chat */}
          <Link
            to="/login"
            className="card-soft p-6 hover:shadow-lg hover:shadow-[#1F4032]/10 transition-all hover:-translate-y-1"
            data-testid="contact-chat"
          >
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#1F4032] to-[#0F2A20] flex items-center justify-center mb-4 shadow-md shadow-[#1F4032]/25">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div className="overline text-[#1F4032]/60 mb-1">Support intégré</div>
            <div className="font-display font-bold text-lg text-[#15302A] mb-2">Suivi de votre mission</div>
            <div className="text-sm text-[#1F4032]/65">
              Connectez-vous pour ouvrir un ticket lié à une mission
            </div>
          </Link>
        </div>

        {/* Address + trust */}
        <div className="mt-10 grid md:grid-cols-2 gap-5">
          <div className="card-soft p-6">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 shrink-0 rounded-2xl bg-[#1F4032]/8 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#1F4032]" />
              </div>
              <div>
                <div className="overline text-[#1F4032]/60 mb-1">Adresse</div>
                <div className="font-display font-bold text-base text-[#15302A] mb-1">MML Logistique</div>
                <div className="text-sm text-[#1F4032]/75 leading-relaxed">
                  Société en cours de création.<br />
                  Les coordonnées légales seront publiées après immatriculation.
                </div>
              </div>
            </div>
          </div>

          <div className="card-soft p-6">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 shrink-0 rounded-2xl bg-[#1F4032]/8 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-[#1F4032]" />
              </div>
              <div>
                <div className="overline text-[#1F4032]/60 mb-1">Confidentialité</div>
                <div className="font-display font-bold text-base text-[#15302A] mb-1">Vos données restent privées</div>
                <div className="text-sm text-[#1F4032]/75 leading-relaxed">
                  Consultez les finalités et les destinataires des données dans notre politique de confidentialité.
                  <Link to="/privacy" className="text-[#1F4032] font-semibold underline underline-offset-2 ml-1">
                    Politique de confidentialité
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Formulaire de contact */}
        <div className="mt-10">
          <ContactForm />
        </div>

        {/* CTA */}
        <div className="mt-12 card-soft-accent p-8 md:p-10 text-center">
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-3">
            Pas encore inscrit ?
          </h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            Créez votre compte en 2 minutes. Vous pouvez publier une mission ou postuler comme transporteur.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/register?role=shipper"
              className="rounded-full bg-white text-[#1F4032] h-12 px-6 inline-flex items-center font-semibold gap-2 hover:bg-white/90 transition-colors"
              data-testid="contact-cta-shipper"
            >
              Envoyer un colis <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/register?role=transporter"
              className="rounded-full bg-white/10 text-white border border-white/30 h-12 px-6 inline-flex items-center font-semibold hover:bg-white/15 transition-colors"
              data-testid="contact-cta-transporter"
            >
              Devenir transporteur
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
